// src/pages/Info.jsx
import React, { useMemo, useState } from "react";

const chipBase =
  "inline-flex items-center rounded-full border px-3 py-1 text-xs sm:text-sm font-medium transition";

const chipInactive =
  "border-slate-700/80 bg-slate-900/80 text-slate-100 hover:border-sky-400 hover:text-sky-200";

const chipActive =
  "border-sky-400/60 bg-sky-500/10 text-sky-100 shadow-[0_0_20px_rgba(56,189,248,0.25)]";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function SectionHeader({ children }) {
  return (
    <h2 className="mb-3 text-xl md:text-2xl font-bold text-slate-50 tracking-tight">
      {children}
    </h2>
  );
}

function ScheduleTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-800/80 bg-slate-950/90 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-900/90 text-slate-300">
          <tr>
            <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide">
              Day
            </th>
            <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide">
              Time
            </th>
            <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide">
              Content
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/80">
          {rows.map((r, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? "bg-slate-950" : "bg-slate-900/70"}
            >
              <td className="px-4 py-2 align-top text-slate-100">{r.day}</td>
              <td className="px-4 py-2 align-top text-slate-200">{r.time}</td>
              <td className="px-4 py-2 align-top text-slate-300">{r.content}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-950/70 p-5">
      <div className="text-slate-100 font-semibold">{title}</div>
      <div className="mt-3 text-sm text-slate-300">{children}</div>
    </div>
  );
}

function TutorialCard({ title, desc, children }) {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-950/90 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
      <div className="px-5 py-4">
        <div className="text-sm font-semibold text-slate-100">{title}</div>
        {desc ? <div className="mt-1 text-sm text-slate-400">{desc}</div> : null}
      </div>
      <div className="border-t border-slate-800/80">{children}</div>
    </div>
  );
}

export default function SchedulePage() {
  // ---- Data (updated per your message) ----
  const nascarRows = [
    { day: "Thursday", time: "Morning", content: "NASCAR Data Uploaded (All Series)" },
    { day: "Friday", time: "Evening", content: "Truck Projections (If Friday Race)" },
    { day: "Saturday", time: "Morning", content: "Trucks and Xfinity Projections (If Race)" },
    { day: "Saturday", time: "Afternoon", content: "Practice & Qualifying Data Updated for the Cup Series." },
    { day: "Saturday", time: "Late Afternoon/Evening", content: "NASCAR DFS Breakdown Video on YouTube." },
    { day: "Saturday", time: "Evening", content: "Projections/Data/Sims Updated for the Cup Series." },
    { day: "Saturday/Sunday", time: "Evening/Morning", content: "Breakdown Article Published." },
    { day: "Sunday", time: "Afternoon", content: "NASCAR Live Show on YouTube." },
  ];

  const nflRows = [
    { day: "Wednesday", time: "10:00 AM", content: "Slate data uploaded; initial projections" },
    { day: "Thursday", time: "—", content: "TNF Showdown — Projections & Data" },
    { day: "Friday", time: "—", content: "Updated projection runs" },
    { day: "Saturday", time: "—", content: "Updated projections + cheat sheets / player pool" },
    { day: "Sunday", time: "—", content: "Final projection runs + SNF Showdown — Projections & Data" },
    { day: "Monday", time: "—", content: "MNF Showdown — Projections & Data" },
  ];

  const mlbRows = [
    { day: "Daily", time: "10:00 AM", content: "Initial data and projections updated" },
    { day: "Daily", time: "5:00–7:00 PM", content: "Cheat sheet + projections/data updated" },
  ];

  const nbaRows = [
    { day: "Daily", time: "10:00 AM", content: "Initial data and projections updated" },
    { day: "Daily", time: "5:00–7:00 PM", content: "Cheat sheet + projections/data updated" },
  ];

  // Tabs
  const TABS = useMemo(
    () => [
      { key: "nascar", label: "NASCAR" },
      { key: "nfl", label: "NFL" },
      { key: "mlb", label: "MLB" },
      { key: "nba", label: "NBA" },
      { key: "videos", label: "Video Tutorials" },
      { key: "discord", label: "Discord" },
      { key: "account", label: "Account" },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState("nascar");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
        {/* Header */}
        <header className="mb-6 rounded-2xl border border-slate-800/80 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-5 py-6 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-950/80 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-100 shadow-[0_0_20px_rgba(56,189,248,0.5)]">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
            Cpenn DFS
          </div>

          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-white">
            Schedule & Info
          </h1>

          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-300">
            Schedules, video walkthroughs, account details, and Discord access — all in one spot.
          </p>

          {/* Tabs */}
          <div className="mt-4 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={cx(
                  chipBase,
                  activeTab === t.key ? chipActive : chipInactive
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>

        {/* Content Panel */}
        <div className="rounded-2xl border border-slate-800/80 bg-slate-950/60 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.7)]">
          {activeTab === "nascar" && (
            <>
              <SectionHeader>NASCAR Weekly Content Release</SectionHeader>
              <ScheduleTable rows={nascarRows} />
            </>
          )}

          {activeTab === "nfl" && (
            <>
              <SectionHeader>NFL Weekly Flow</SectionHeader>
              <ScheduleTable rows={nflRows} />
            </>
          )}

          {activeTab === "mlb" && (
            <>
              <SectionHeader>MLB Daily Flow</SectionHeader>
              <ScheduleTable rows={mlbRows} />
            </>
          )}

          {activeTab === "nba" && (
            <>
              <SectionHeader>NBA Daily Flow</SectionHeader>
              <ScheduleTable rows={nbaRows} />
            </>
          )}

          {activeTab === "videos" && (
            <>
              <SectionHeader>Video Tutorials</SectionHeader>
              <div className="text-sm text-slate-300 mb-4">
                Step-by-step walkthroughs. More videos will be added here.
              </div>

              <TutorialCard
                title="NASCAR Content Tutorial"
                desc="Navigating the Entire NASCAR Website"
              >
                {/* Vimeo embed (responsive 16:9) */}
                <div className="overflow-hidden">
                  <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                    <iframe
                      src="https://player.vimeo.com/video/1157983360?badge=0&autopause=0&player_id=0&app_id=58479"
                      title="NASCAR Content Tutorial"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                </div>
              </TutorialCard>
            </>
          )}

          {activeTab === "discord" && (
            <>
              <SectionHeader>Discord</SectionHeader>

              <div className="space-y-4">
                <InfoCard title="How to join">
                  <ol className="space-y-2 list-decimal pl-5">
                    <li>
                      Go to the <span className="text-slate-100 font-semibold">Account</span> page and click{" "}
                      <span className="text-slate-100 font-semibold">Connect Discord</span>.
                    </li>
                    <li>
                      If you’re not already in Discord, you’ll be prompted to accept an invite and join.
                    </li>
                    <li>
                      Once connected, press <span className="text-slate-100 font-semibold">Role Sync</span>{" "}
                      to be placed into the correct channels based on your subscription.
                    </li>
                  </ol>
                </InfoCard>

                <InfoCard title="Access by plan">
                  <ul className="space-y-2 list-disc pl-5">
                    <li>
                      <span className="text-slate-100 font-semibold">Free users</span>: limited to{" "}
                      <span className="text-slate-100 font-semibold">General</span>.
                    </li>
                    <li>
                      <span className="text-slate-100 font-semibold">Subscribers</span>: Role Sync unlocks your
                      plan/sport-specific channels automatically.
                    </li>
                  </ul>
                </InfoCard>

                <InfoCard title="Troubleshooting">
                  <ul className="space-y-2 list-disc pl-5">
                    <li>
                      Joined Discord but can’t see channels? Hit{" "}
                      <span className="text-slate-100 font-semibold">Role Sync</span> again.
                    </li>
                    <li>Make sure you connected the correct Discord account.</li>
                    <li>If you changed plans, run Role Sync to refresh access.</li>
                  </ul>
                </InfoCard>
              </div>
            </>
          )}

          {activeTab === "account" && (
            <>
              <SectionHeader>Account</SectionHeader>

              <div className="space-y-4">
                <InfoCard title="How subscribing works">
                  <ol className="space-y-2 list-decimal pl-5">
                    <li>Choose a plan on the site and complete checkout.</li>
                    <li>
                      Once payment is confirmed, your account unlocks the content for that plan automatically.
                    </li>
                    <li>
                      If your plan includes Discord access, connect Discord and run Role Sync in the Discord tab.
                    </li>
                  </ol>
                </InfoCard>

                <InfoCard title="Cancel vs pause">
                  <ul className="space-y-2 list-disc pl-5">
                    <li>
                      <span className="text-slate-100 font-semibold">Pause</span>: your plan stops renewing while paused.
                      You keep access until your current paid period ends, then access downgrades.
                    </li>
                    <li>
                      <span className="text-slate-100 font-semibold">Cancel</span>: your plan won’t renew.
                      You keep access through the end of your current paid period, then access downgrades.
                    </li>
                  </ul>
                  <div className="mt-3 text-xs text-slate-400">
                    If you resubscribe later, access comes back automatically.
                  </div>
                </InfoCard>

                <InfoCard title="Switching plans mid-cycle">
                  <div className="text-sm text-slate-300">
                    If you’re trying to switch plans but the system won’t let you because you’re in the middle of a billing cycle,
                    contact me and we’ll work something out.
                  </div>
                </InfoCard>

                <InfoCard title="What happens when a plan changes">
                  <ul className="space-y-2 list-disc pl-5">
                    <li>Upgrading unlocks the new plan content as soon as it’s active.</li>
                    <li>If you downgrade, pause, or cancel, you keep access until the end of your paid period.</li>
                    <li>For Discord access changes, run Role Sync to refresh your channels.</li>
                  </ul>
                </InfoCard>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
